import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LinearProgress,
  Link,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  CalendarMonthOutlined,
  CheckCircleOutline,
  ChecklistOutlined,
  GroupsOutlined,
  InfoOutlined,
  LinkOutlined,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { AccountSection } from '../accounts/AccountSection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminCXAPI, cxError } from '../../../lib/apiCX';
import { useProfileDraft } from '../organizations/ProfileDrafts';
import type { CXCustomer, CXProgressStep, CXStepStatus } from '../../../types/cx';
import { AutomaticTexts } from './AutomaticTexts';

const states: { value: CXStepStatus; label: string }[] = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'complete', label: 'Complete' },
  { value: 'not_applicable', label: 'Not applicable' },
];

function CustomerPlanEditor({
  customerId,
  saved,
  onSaved,
  onReload,
}: {
  customerId: string;
  saved: CXCustomer;
  onSaved: (data: CXCustomer) => void;
  onReload: () => void;
}) {
  const [steps, setSteps] = useState(saved.steps);
  const [source, setSource] = useState(saved.source_system);
  const [revision, setRevision] = useState(saved.revision);
  const [deadline, setDeadline] = useState('');
  const [reason, setReason] = useState('');
  const change = (key: string, value: Partial<CXProgressStep>) =>
    setSteps((previous) => ({
      ...previous,
      [key]: {
        ...(previous[key] || { status: 'not_started', notes: '' }),
        ...value,
      },
    }));
  const save = useMutation({
    mutationFn: () =>
      AdminCXAPI.saveCustomer(customerId, {
        revision,
        steps,
        source_system: source,
      }),
    onSuccess: (data) => {
      setSteps(data.steps);
      setRevision(data.revision);
      onSaved(data);
    },
  });
  const extend = useMutation({
    mutationFn: () => {
      const date = new Date(deadline);
      if (!Number.isFinite(date.getTime()) || !saved.subscription?.trial_end)
        throw new Error('Choose a valid new deadline.');
      return AdminCXAPI.extendTrial(customerId, {
        expected_trial_end: saved.subscription.trial_end,
        new_trial_end: date.toISOString(),
        reason,
      });
    },
    onSuccess: (data) => {
      setDeadline('');
      setReason('');
      onSaved(data);
    },
  });
  const dirty =
    JSON.stringify(steps) !== JSON.stringify(saved.steps) ||
    source !== saved.source_system ||
    !!deadline ||
    !!reason;
  useProfileDraft('onboarding-plan', dirty, save.isPending || extend.isPending);
  const done = saved.definitions.filter((step) =>
    ['complete', 'not_applicable'].includes(steps[step.key]?.status || ''),
  ).length;
  const next = saved.definitions.find(
    (step) =>
      step.track === 'sequential' &&
      !['complete', 'not_applicable'].includes(steps[step.key]?.status || 'not_started'),
  );
  const skipped = saved.definitions.filter((step) => steps[step.key]?.status === 'not_applicable').length;
  const renderStep = (step: CXCustomer['definitions'][number], index: number) => {
    const status = steps[step.key]?.status || 'not_started';
    const evidence = saved.automation?.[step.key];
    const automatic =
      !!evidence &&
      (steps[step.key]?.tracking || (status === 'not_started' ? 'automatic' : 'manual')) === 'automatic';
    const statusLabel = states.find((state) => state.value === status)?.label || 'Not started';
    const hasMaterials = !!(step.checklist || step.call_script || step.guide_url || step.video_url);
    return (
      <Sheet
        component="section"
        aria-label={step.title}
        variant="outlined"
        key={step.key}
        sx={{
          borderRadius: 'md',
          overflow: 'hidden',
          borderLeftWidth: 4,
          borderLeftColor:
            status === 'blocked' ? 'danger.500' : status === 'complete' ? 'success.500' : 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            bgcolor: 'background.level1',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: 'background.surface',
              fontWeight: 'lg',
              color: 'text.secondary',
            }}
          >
            {step.track === 'parallel' ? <GroupsOutlined fontSize="small" /> : index + 1}
          </Box>
          <Box sx={{ flex: '1 1 170px' }}>
            <Typography component="h4" level="title-md">
              {step.title}
            </Typography>
            {evidence && (
              <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {automatic ? 'Automatically tracked' : 'Manual override'}
              </Typography>
            )}
            {next?.key === step.key && (
              <Chip size="sm" color="primary" variant="soft" sx={{ mt: 0.5 }}>
                Next step
              </Chip>
            )}
          </Box>
          <FormControl sx={{ width: { xs: '100%', sm: 270 } }}>
            <FormLabel aria-label={`${step.title} status`}>Status</FormLabel>
            <Select
              aria-label={`${step.title} status`}
              value={automatic ? 'automatic' : status}
              color={status === 'blocked' ? 'danger' : status === 'complete' ? 'success' : 'neutral'}
              onChange={(event, value) => {
                if (!event || !value) return;
                if (value === 'automatic') {
                  change(step.key, {
                    tracking: 'automatic',
                    status: evidence?.status || 'not_started',
                  });
                } else {
                  change(step.key, {
                    status: value as CXStepStatus,
                    ...(evidence ? { tracking: 'manual' as const } : {}),
                  });
                }
              }}
            >
              {evidence && (
                <Option value="automatic" disabled={!evidence.available && !automatic}>
                  Automatic · {automatic ? statusLabel : 'Use activity'}
                </Option>
              )}
              {states.map((state) => (
                <Option key={state.value} value={state.value}>
                  {state.label}
                </Option>
              ))}
            </Select>
          </FormControl>
        </Box>
        {evidence && (
          <Box sx={{ px: 2, py: 1.5, display: 'grid', gap: 0.75 }}>
            {evidence.available ? (
              <>
                {evidence.criteria.map((item) => (
                  <Typography
                    key={item.label}
                    level="body-sm"
                    startDecorator={
                      item.met ? (
                        <CheckCircleOutline color="success" fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked fontSize="small" />
                      )
                    }
                    sx={{ alignItems: 'flex-start', color: 'text.secondary' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clipPath: 'inset(50%)',
                      }}
                    >
                      {item.met ? 'Met: ' : 'Not yet: '}
                    </Box>
                    {item.label}
                  </Typography>
                ))}
                {automatic && status === 'complete' && evidence.status !== 'complete' && (
                  <Typography level="body-xs">
                    Previously completed. Later changes do not reset this milestone.
                  </Typography>
                )}
              </>
            ) : (
              <Typography level="body-sm" color="warning">
                Activity could not be checked. The saved status is unchanged. Refresh to try again.
              </Typography>
            )}
          </Box>
        )}
        <Box
          component="details"
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& > summary': {
              cursor: 'pointer',
              px: 2,
              py: 1.5,
              fontWeight: 'lg',
              fontSize: 'sm',
              '&:hover': { bgcolor: 'background.level1' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.500',
                outlineOffset: -2,
              },
            },
          }}
        >
          <summary>
            Notes &amp; resources
            {steps[step.key]?.notes ? ' · Notes added' : ''}
          </summary>
          <Box
            sx={{
              p: 2,
              pt: 0.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: hasMaterials ? '1fr 1fr' : '1fr',
              },
              gap: 2.5,
            }}
          >
            <FormControl>
              <FormLabel>Progress / blocker notes</FormLabel>
              <Textarea
                minRows={3}
                value={steps[step.key]?.notes || ''}
                onChange={(e) => change(step.key, { notes: e.target.value })}
                placeholder="What is done, what is blocking progress, and who will follow up?"
              />
              <FormHelperText>
                Internal notes for this account. Save onboarding progress to keep your changes.
              </FormHelperText>
            </FormControl>
            {hasMaterials ? (
              <Sheet
                variant="soft"
                sx={{
                  p: 2,
                  borderRadius: 'md',
                  display: 'grid',
                  gap: 1.5,
                  alignContent: 'start',
                }}
              >
                <Typography level="title-sm">Team reference materials</Typography>
                {step.checklist && (
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 'lg', mb: 0.5 }}>
                      Checklist &amp; instructions
                    </Typography>
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                      {step.checklist}
                    </Typography>
                  </Box>
                )}
                {step.call_script && (
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 'lg', mb: 0.5 }}>
                      Call script
                    </Typography>
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                      {step.call_script}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {step.guide_url && (
                    <Link href={step.guide_url} target="_blank" rel="noopener noreferrer">
                      Help article ↗
                    </Link>
                  )}
                  {step.video_url && (
                    <Link href={step.video_url} target="_blank" rel="noopener noreferrer">
                      Video ↗
                    </Link>
                  )}
                </Box>
              </Sheet>
            ) : (
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                No reference materials for this step. Add guides and call scripts in Accounts settings →
                Onboarding playbook.
              </Typography>
            )}
          </Box>
        </Box>
      </Sheet>
    );
  };
  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <AccountSection
        title="Customer onboarding plan"
        description="Progress updates automatically from account activity. Review the criteria below or choose a status to override a step."
        icon={<ChecklistOutlined />}
        help="Automatic milestones stay completed after later deletions. Manual overrides are preserved until you choose Automatic again. Team and mobile app steps are still tracked manually."
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              level="body-xs"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'text.secondary',
              }}
            >
              What to work on next
            </Typography>
            <Typography level="title-lg">{next ? next.title : 'All sequential steps finished'}</Typography>
          </Box>
          <Typography level="body-sm">
            {done} of {saved.definitions.length} steps finished
            {skipped ? ` · ${skipped} not applicable` : ''}
          </Typography>
        </Box>
        <LinearProgress
          aria-label="Onboarding progress"
          determinate
          value={saved.definitions.length ? (done / saved.definitions.length) * 100 : 0}
          sx={{ '--LinearProgress-thickness': '8px' }}
        />
        <Button
          variant="outlined"
          size="sm"
          sx={{ alignSelf: 'start' }}
          disabled={dirty || save.isPending}
          onClick={onReload}
        >
          Refresh progress
        </Button>
        <Box
          component="details"
          sx={{
            '& > summary': {
              cursor: 'pointer',
              fontWeight: 'lg',
              fontSize: 'sm',
            },
          }}
        >
          <summary>
            Previous system, booking &amp; app links
            {source ? ` · ${source}` : ''}
          </summary>
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            <FormControl>
              <FormLabel>Previous system</FormLabel>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder={saved.migration_sources.join(' / ')}
              />
              <FormHelperText>
                The software they are moving from. Use this to plan data imports.
              </FormHelperText>
            </FormControl>
            <Box>
              <Typography level="title-sm" startDecorator={<LinkOutlined fontSize="small" />}>
                Booking &amp; app links
              </Typography>
              <Typography level="body-xs" sx={{ mt: 0.5, mb: 1 }}>
                Open these resources while helping the customer get set up.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  [saved.setup_booking_url, 'Book setup call'],
                  [saved.marketing_booking_url, 'Book marketing call'],
                  [saved.ios_app_url, 'iOS app'],
                  [saved.android_app_url, 'Android app'],
                ]
                  .filter(([url]) => url)
                  .map(([url, label]) => (
                    <Link key={label} href={url} target="_blank" rel="noopener noreferrer">
                      {label} ↗
                    </Link>
                  ))}
              </Box>
              {![
                saved.setup_booking_url,
                saved.marketing_booking_url,
                saved.ios_app_url,
                saved.android_app_url,
              ].some(Boolean) && (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  No links configured. Add them in Accounts settings → Onboarding playbook.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </AccountSection>
      {(['sequential', 'parallel'] as const).map((track) => {
        const group = saved.definitions.filter((step) => step.track === track);
        if (!group.length) return null;
        return (
          <Box
            component="section"
            aria-label={track === 'sequential' ? 'Onboarding steps' : 'Throughout onboarding'}
            key={track}
            sx={{ display: 'grid', gap: 1.5 }}
          >
            <Box>
              <Typography component="h3" level="title-lg">
                {track === 'sequential' ? 'Onboarding steps' : 'Throughout onboarding'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {track === 'sequential'
                  ? 'Work through these in order. Open a step’s notes and resources for guidance.'
                  : 'Work on these alongside the main steps, whenever the customer is ready.'}
              </Typography>
            </Box>
            {group.map(renderStep)}
          </Box>
        );
      })}
      {save.error && (
        <Alert color="danger" sx={{ flexWrap: 'wrap' }}>
          {cxError(save.error)}
          <Button color="danger" variant="outlined" onClick={onReload}>
            Reload plan (discard edits)
          </Button>
        </Alert>
      )}
      <Sheet
        variant="soft"
        sx={{
          p: 2,
          borderRadius: 'md',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography level="body-sm">
          Automatic progress is saved for you. Save any overrides, notes, or previous-system changes.
        </Typography>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save onboarding progress
        </Button>
      </Sheet>
      <AutomaticTexts customerId={customerId} />
      {saved.subscription && (
        <AccountSection
          title="Trial and subscription"
          description="Review the billing timeline. Trial extensions are saved separately from onboarding progress."
          icon={<CalendarMonthOutlined />}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Chip variant="soft">{saved.subscription.status}</Chip>
            <Typography level="body-sm">
              {saved.subscription.trial_end
                ? `Trial deadline: ${new Date(saved.subscription.trial_end).toLocaleString()}`
                : 'No trial deadline'}
            </Typography>
          </Box>
          {saved.subscription.can_extend && (
            <>
              <Typography level="body-sm">{saved.trial_extension_instructions}</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <FormControl>
                  <FormLabel>
                    New trial deadline ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Reason for extension</FormLabel>
                  <Textarea minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </FormControl>
              </Box>
              <Alert variant="soft" color="neutral" startDecorator={<InfoOutlined />}>
                Extending changes the trial deadline in Stripe and ApexOS, postponing automatic paid billing.
              </Alert>
              <Button
                sx={{ justifySelf: 'start' }}
                loading={extend.isPending}
                disabled={!deadline || reason.trim().length < 3}
                onClick={() => extend.mutate()}
              >
                Extend trial in Stripe and ApexOS
              </Button>
            </>
          )}
          {extend.error && <Alert color="danger">{cxError(extend.error)}</Alert>}
        </AccountSection>
      )}
    </Box>
  );
}

export function CXCustomerPlan({ customerId }: { customerId: string }) {
  const cache = useQueryClient();
  const key = ['admin', 'cx', 'customer', customerId];
  const query = useQuery({
    queryKey: key,
    queryFn: () => AdminCXAPI.customer(customerId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  if (query.isPending) return <LinearProgress />;
  if (query.error || !query.data)
    return (
      <Alert color="danger">
        {cxError(query.error)}
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Alert>
    );
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {notice && <Alert color="success">{notice}</Alert>}
      <CustomerPlanEditor
        key={`${customerId}:${reload}`}
        customerId={customerId}
        saved={query.data}
        onReload={async () => {
          const result = await query.refetch();
          if (!result.error) {
            setReload((value) => value + 1);
            setNotice('Progress refreshed.');
          }
        }}
        onSaved={(data) => {
          cache.setQueryData(key, data);
          cache.invalidateQueries({
            queryKey: ['admin', 'customer', customerId],
          });
          cache.invalidateQueries({ queryKey: ['admin-orgs'] });
          cache.invalidateQueries({ queryKey: ['admin-org'] });
          setNotice('Customer plan updated.');
        }}
      />
    </Box>
  );
}
